import React, { useRef } from 'react'
import { db } from '../'
import { ref, push, get, set } from 'firebase/database'
import { useNavigate, useSearchParams } from 'react-router-dom';

const Join = ({userData}) => {
  const gameID = useRef(null);
  const navigate = useNavigate();
  const [error, setError] = React.useState(null);

  // URLパラメータ取得
  const [searchParams] = useSearchParams();
  const paramGameID = searchParams.get("gameId"); // ?gameId=123 のとき、"123" を取得
  const paramPersonalGameID = searchParams.get("personalGameId"); // ?personalGameId=ABC123-K7M9P2 のとき取得

  // 個人ゲームID解析関数
  const parsePersonalGameId = (personalGameId) => {
    if (!personalGameId || !personalGameId.includes('-')) {
      return null;
    }
    const [gameId, memberCode] = personalGameId.split('-');
    return { gameId, memberCode };
  };

  // URLパラメータがあれば、ゲームIDをセット
  React.useEffect(() => {
    if(paramPersonalGameID) {
      gameID.current.value = paramPersonalGameID;
    } else if(paramGameID) {
      gameID.current.value = paramGameID;
    }
  }, [paramGameID, paramPersonalGameID]);

  // ゲームに参加
  const joinGame = async (e) => {
    e.preventDefault();

    const inputValue = gameID.current.value;
    
    // 入力値が個人ゲームIDかどうか判定
    const personalGameIdData = parsePersonalGameId(inputValue);
    
    if (personalGameIdData) {
      // 個人ゲームIDの場合
      await joinWithPersonalGameId(personalGameIdData.gameId, personalGameIdData.memberCode);
    } else {
      // 通常のゲームIDの場合
      await joinWithGameId(inputValue);
    }
  }

  // 個人ゲームIDでの参加処理
  const joinWithPersonalGameId = async (gameId, memberCode) => {
    try {
      // 既に参加済みでないか確認
      const userRef = ref(db, 'users/' + userData.uid + '/joinedGames/');
      const userSnapshot = await get(userRef);
      
      if (userSnapshot.exists() && Object.values(userSnapshot.val()).includes(gameId)) {
        setError("既に参加済みです");
        return;
      }

      // ゲームデータの存在確認
      const gameRef = ref(db, 'games/' + gameId);
      const gameSnapshot = await get(gameRef);
      
      if (!gameSnapshot.exists()) {
        setError("ゲームが存在しません");
        return;
      }

      const gameData = gameSnapshot.val();
      
      // メンバー内でmemberCodeを持つ事前登録参加者を検索
      const preRegisteredMember = gameData.members ? Object.values(gameData.members).find(
        member => member.memberCode === memberCode && member.registered
      ) : null;
      
      if (!preRegisteredMember) {
        setError("無効な個人ゲームIDです");
        return;
      }

      // 既に参加登録済みかチェック（uid が実際のユーザーのものかで判定）
      if (preRegisteredMember.uid && !preRegisteredMember.uid.startsWith('manual_')) {
        setError("この個人ゲームIDは既に使用されています");
        return;
      }

      // アクティベート状態確認
      const activatedRef = ref(db, 'activatedGames/' + gameId);
      const activatedSnapshot = await get(activatedRef);
      
      if (activatedSnapshot.exists() && activatedSnapshot.val().activated) {
        setError("アクティベートされたゲームには参加できません");
        return;
      }

      // 参加処理実行
      await joinProcessWithPreRegistration(gameId, memberCode, preRegisteredMember);
      
    } catch (error) {
      console.error('Error joining with personal game ID:', error);
      setError("参加登録に失敗しました");
    }
  };

  // 通常のゲームIDでの参加処理（従来の処理）
  const joinWithGameId = async (gameId) => {
    try {
      // 既に参加済みでないか確認
      const userRef = ref(db, 'users/' + userData.uid + '/joinedGames/');
      const userSnapshot = await get(userRef);

      if (userSnapshot.exists() && Object.values(userSnapshot.val()).includes(gameId)) {
        setError("既に参加済みです");
        return;
      }

      // ゲームデータが存在し、参加登録を受け付けているか確認
      const gameRef = ref(db, 'games/' + gameId);
      const gameSnapshot = await get(gameRef);
      
      if (!gameSnapshot.exists() || !gameSnapshot.val().info.public) {
        setError("ゲームが存在しないか、参加登録を受け付けていません");
        return;
      }

      // アクティベート状態確認
      const activatedRef = ref(db, 'activatedGames/' + gameId);
      const activatedSnapshot = await get(activatedRef);
      
      if (activatedSnapshot.exists() && activatedSnapshot.val().activated) {
        setError("アクティベートされたゲームには参加できません");
        return;
      }

      // 従来の参加処理
      await joinProcess(gameId);
      
    } catch (error) {
      console.error('Error joining with game ID:', error);
      setError("参加登録に失敗しました");
    }
  };

  // 事前登録データを使った参加処理
  const joinProcessWithPreRegistration = async (gameId, memberCode, preMember) => {
    try {
      // 既存のメンバーデータを更新（実際のユーザー情報を設定）
      const gameRef = ref(db, 'games/' + gameId + '/members');
      const membersSnapshot = await get(gameRef);
      
      if (membersSnapshot.exists()) {
        const members = membersSnapshot.val();
        
        // memberCodeが一致するメンバーを見つけて更新
        for (const [key, member] of Object.entries(members)) {
          if (member.memberCode === memberCode && member.registered) {
            // 実際のユーザー情報でメンバーデータを更新
            await set(ref(db, `games/${gameId}/members/${key}`), {
              ...member,
              uid: userData.uid,
              name: userData.username,
              email: userData.email,
              joinedAt: new Date().toISOString()
            });
            break;
          }
        }
      }

      // ユーザーの参加ゲーム一覧に追加
      await set(ref(db, 'users/' + userData.uid + '/joinedGames/' + gameId), gameId);

      // チーム情報の取得
      const gameDataRef = ref(db, 'games/' + gameId);
      const gameDataSnapshot = await get(gameDataRef);
      const gameData = gameDataSnapshot.val();
      const teams = gameData.teams || [];
      const teamName = preMember.team !== null && preMember.team !== undefined ? 
        teams[preMember.team]?.name || `チーム${preMember.team + 1}` : 
        '未設定';

      alert(`参加登録が完了しました\nチーム: ${teamName}`);
      navigate('/');
      
    } catch (error) {
      console.error('Error in joinProcessWithPreRegistration:', error);
      setError("参加登録に失敗しました");
    }
  };

  // ゲームに参加する処理（従来）
  const joinProcess = (gameId) => {
    return push(ref(db, 'games/' + gameId + '/members/'), {
      uid: userData.uid,
      name: userData.username,
      email: userData.email
    })
    .then(() => {
      return set(ref(db, 'users/' + userData.uid + '/joinedGames/' + gameId), gameId)
      .then(() => {
        alert("参加登録が完了しました");
        navigate('/');
      })
    })
    .catch((error) => {
      console.error('Error in joinProcess:', error);
      throw error;
    });
  }

  return (
    <div className='my-4 px-3'>
        <h2 className='text-center text-2xl mb-6'>ゲームに参加</h2>
        <form className='mx-auto max-w-xl' onSubmit={(e) => joinGame(e)}>
            <label>
                ゲームID または 個人ゲームID<br />
                <input ref={gameID} type="text" name="gameCode" className='w-full mb-2 p-2 rounded-lg border focus:outline-purple-300' placeholder='ABC123 または ABC123-K7M9P2' />
                <div className='text-xs text-gray-500 mb-4'>
                    通常のゲームID（例：ABC123）または個人ゲームID（例：ABC123-K7M9P2）を入力してください
                </div>
            </label>

            {error && <p className='text-red-700 bg-red-100 p-2 rounded-md text-base mb-4'><i className='bi bi-exclamation-triangle mx-2'></i>{error}</p>}

            <button type="submit" className='block text-center rounded-lg bg-purple-500 hover:bg-purple-600 p-2 w-full text-white text-lg'>参加する</button>
        </form>
    </div>
  )
}

export default Join